import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import {
  Dropdown,
  Field,
  Option,
  Switch,
  mergeClasses,
} from '@fluentui/react-components';
import {
  DAY_OF_WEEK,
  isValidDayOfWeek,
  isValidDaysOfWeek,
} from '../../services/contractorAvailabilityRuleService';
import {
  TimeSelect,
  TimezoneSelect,
  isValidTimeOfDay,
  isValidTimeZone,
  timeOfDayToSeconds,
  useFormLayoutStyles,
  useIsMobile,
} from '@moko/ui';
import {
  dateOnlyToPickerDate,
  fromApiDateOnly,
  toApiDateOnly,
} from '../../utils/dateOnly';

export type ContractorAvailabilityFormValues = {
  daysOfWeek: number[];
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  timezoneId: string;
  effectiveFrom: string;
  effectiveTo: string;
  status: boolean;
};

export interface ContractorAvailabilityFormProps {
  disabled: boolean;
  multipleDays?: boolean;
}

export const ContractorAvailabilityForm = ({
  disabled,
  multipleDays = false,
}: ContractorAvailabilityFormProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const styles = useFormLayoutStyles();
  const { control, getValues, trigger, watch, formState } =
    useFormContext<ContractorAvailabilityFormValues>();
  const { errors, isSubmitted } = formState;
  const daysOfWeek = watch('daysOfWeek') ?? [];
  const dayOfWeek = watch('dayOfWeek');
  const selectedDayLabels = DAY_OF_WEEK.filter((option) =>
    daysOfWeek.includes(option.value)
  ).map((option) => t(option.key, option.fallback));
  const selectedDay = DAY_OF_WEEK.find((option) => option.value === dayOfWeek);

  return (
    <>
      {multipleDays ? (
        <Controller
          control={control}
          name="daysOfWeek"
          rules={{
            validate: (value) =>
              (isValidDaysOfWeek(value) && value.length > 0) ||
              t('dayOfWeekInvalid', 'Select at least one valid day of week.'),
          }}
          render={({ field }) => (
            <Field
              label={t('dayOfWeek', 'Day of week')}
              required
              validationState={errors.daysOfWeek ? 'error' : 'none'}
              validationMessage={errors.daysOfWeek?.message}
            >
              <Dropdown
                style={{ minWidth: 0, width: '100%' }}
                multiselect
                value={selectedDayLabels.join(', ')}
                selectedOptions={(field.value ?? []).map(String)}
                onOptionSelect={(_, data) => {
                  const next = (data.selectedOptions ?? [])
                    .map((raw) => Number(raw))
                    .filter(isValidDayOfWeek);
                  field.onChange(
                    Array.from(new Set(next)).sort((a, b) => a - b)
                  );
                }}
                disabled={disabled}
              >
                {DAY_OF_WEEK.map((option) => (
                  <Option key={option.value} value={String(option.value)}>
                    {t(option.key, option.fallback)}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          )}
        />
      ) : (
        <Controller
          control={control}
          name="dayOfWeek"
          rules={{
            validate: (value) =>
              isValidDayOfWeek(value) ||
              t('dayOfWeekInvalid', 'Select a valid day of week.'),
          }}
          render={({ field }) => (
            <Field
              label={t('dayOfWeek', 'Day of week')}
              required
              validationState={errors.dayOfWeek ? 'error' : 'none'}
              validationMessage={errors.dayOfWeek?.message}
            >
              <Dropdown
                style={{ minWidth: 0, width: '100%' }}
                value={
                  selectedDay ? t(selectedDay.key, selectedDay.fallback) : ''
                }
                selectedOptions={
                  isValidDayOfWeek(field.value) ? [String(field.value)] : []
                }
                onOptionSelect={(_, data) => {
                  const next = Number(data.optionValue);
                  if (isValidDayOfWeek(next)) field.onChange(next);
                }}
                disabled={disabled}
              >
                {DAY_OF_WEEK.map((option) => (
                  <Option key={option.value} value={String(option.value)}>
                    {t(option.key, option.fallback)}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          )}
        />
      )}

      <div
        className={mergeClasses(
          styles.twoCol,
          isMobile && styles.twoColStacked
        )}
      >
        <div className={styles.col}>
          <Controller
            control={control}
            name="startTime"
            rules={{
              required: t('startTimeRequired', 'Start time is required.'),
              validate: (value) =>
                isValidTimeOfDay(value) ||
                t('startTimeInvalid', 'Start time is invalid.'),
            }}
            render={({ field }) => (
              <Field
                label={t('startTime', 'Start time')}
                required
                validationState={errors.startTime ? 'error' : 'none'}
                validationMessage={errors.startTime?.message}
              >
                <TimeSelect
                  value={field.value}
                  disabled={disabled}
                  onChange={(next) => {
                    field.onChange(next);
                    if (isSubmitted) void trigger('endTime');
                  }}
                />
              </Field>
            )}
          />
        </div>
        <div className={styles.col}>
          <Controller
            control={control}
            name="endTime"
            rules={{
              required: t('endTimeRequired', 'End time is required.'),
              validate: (value) => {
                if (!isValidTimeOfDay(value)) {
                  return t('endTimeInvalid', 'End time is invalid.');
                }
                const start = timeOfDayToSeconds(getValues('startTime'));
                const end = timeOfDayToSeconds(value);
                if (start != null && end != null && end <= start) {
                  return t(
                    'startTimeMustBeBeforeEndTime',
                    'Start time must be earlier than End time.'
                  );
                }
                return true;
              },
            }}
            render={({ field }) => (
              <Field
                label={t('endTime', 'End time')}
                required
                validationState={errors.endTime ? 'error' : 'none'}
                validationMessage={errors.endTime?.message}
              >
                <TimeSelect
                  value={field.value}
                  disabled={disabled}
                  onChange={field.onChange}
                />
              </Field>
            )}
          />
        </div>
      </div>

      <Controller
        control={control}
        name="timezoneId"
        rules={{
          required: t(
            'timezoneInvalid',
            'Timezone must be a valid timezone ID.'
          ),
          validate: (value) =>
            isValidTimeZone(value) ||
            t('timezoneInvalid', 'Timezone must be a valid timezone ID.'),
        }}
        render={({ field }) => (
          <Field
            label={t('timezone', 'Timezone')}
            required
            validationState={errors.timezoneId ? 'error' : 'none'}
            validationMessage={errors.timezoneId?.message}
          >
            <TimezoneSelect
              value={field.value}
              placeholder={t('selectTimezone', 'Select timezone')}
              disabled={disabled}
              onChange={field.onChange}
            />
          </Field>
        )}
      />

      <div
        className={mergeClasses(
          styles.twoCol,
          isMobile && styles.twoColStacked
        )}
      >
        <div className={styles.col}>
          <Controller
            control={control}
            name="effectiveFrom"
            rules={{
              required: t(
                'effectiveFromRequired',
                'Effective from is required.'
              ),
              validate: (value) =>
                !!fromApiDateOnly(value) ||
                t('effectiveFromRequired', 'Effective from is required.'),
            }}
            render={({ field }) => (
              <Field
                label={t('effectiveFrom', 'Effective from')}
                required
                validationState={errors.effectiveFrom ? 'error' : 'none'}
                validationMessage={errors.effectiveFrom?.message}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  value={dateOnlyToPickerDate(field.value)}
                  disabled={disabled}
                  onSelectDate={(selected) => {
                    field.onChange(toApiDateOnly(selected));
                    if (isSubmitted) void trigger('effectiveTo');
                  }}
                />
              </Field>
            )}
          />
        </div>
        <div className={styles.col}>
          <Controller
            control={control}
            name="effectiveTo"
            rules={{
              required: t('effectiveToRequired', 'Effective to is required.'),
              validate: (value) => {
                const effectiveTo = fromApiDateOnly(value);
                if (!effectiveTo) {
                  return t('effectiveToRequired', 'Effective to is required.');
                }
                const effectiveFrom = fromApiDateOnly(
                  getValues('effectiveFrom')
                );
                if (effectiveFrom && effectiveTo < effectiveFrom) {
                  return t(
                    'effectiveToNotBeforeFrom',
                    'Effective to must not be before Effective from.'
                  );
                }
                return true;
              },
            }}
            render={({ field }) => (
              <Field
                label={t('effectiveTo', 'Effective to')}
                required
                validationState={errors.effectiveTo ? 'error' : 'none'}
                validationMessage={errors.effectiveTo?.message}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  value={dateOnlyToPickerDate(field.value)}
                  disabled={disabled}
                  onSelectDate={(selected) =>
                    field.onChange(toApiDateOnly(selected))
                  }
                />
              </Field>
            )}
          />
        </div>
      </div>

      <Controller
        control={control}
        name="status"
        render={({ field }) => (
          <Field
            label={t('status', 'Status')}
            orientation="horizontal"
            className={styles.toggleField}
          >
            <Switch
              checked={field.value}
              label={
                field.value ? t('active', 'Active') : t('inactive', 'Inactive')
              }
              onChange={(_, data) => field.onChange(data.checked)}
              disabled={disabled}
            />
          </Field>
        )}
      />
    </>
  );
};
